import React, {useState} from 'react';
import {PageTitle} from '../../../components/page/page_title.tsx';
import {__, transString} from '../../../support/lang/translations.ts';
import {Table} from '../../../components/table/table.tsx';
import {TableRow} from '../../../components/table/table_row.tsx';
import {TableCell} from '../../../components/table/table_cell.tsx';
import {useNavigate, useParams} from 'react-router-dom';
import {useAppSelector} from '../../../store/store.ts';
import {Status} from '../../../enums/status.ts';
import {CenteredSpinner} from '../../../components/spinner/centered_spinner.tsx';
import {Card} from '../../../components/card/card.tsx';
import {Button} from '../../../components/button/button.tsx';
import {ButtonPosition} from '../../../enums/button_position.ts';
import {FlexRow} from '../../../components/utility/flex_row.tsx';
import {Icon} from '../../../components/icon/icon.tsx';
import {FlexColumn} from '../../../components/utility/flex_column.tsx';
import {Alignment} from '../../../enums/alignment.ts';
import {Switch} from '../../../components/switch/switch.tsx';
import {Form} from '../../../components/form/form.tsx';
import {VehicleSpecsGrid} from '../../../components/vehicle/vehicle_specs_grid.tsx';
import {DeleteVehicleModal} from '../../../components/vehicle/delete_vehicle_modal.tsx';
import {useIsNavigatingOrPending} from '../../../support/use_is_navigating_or_pending.ts';
import {formatKm} from '../../../support/format_distance.ts';
import {HideElement} from '../../../components/utility/hide_element_wrapper.tsx';
import {ShowElement} from '../../../components/utility/show_element_wrapper.tsx';
import {AltButton} from '../../../components/button/alt_button.tsx';
import {NotificationType} from '../../notifications/notification.tsx';

interface OwnProps {
    children?: React.ReactNode;
}

export function Details({children}: OwnProps) {
    const params = useParams<'vehicleId'>();
    const vehicleId = parseInt(params.vehicleId ?? '-1', 10);
    const customerInfo = useAppSelector((state) => state.customerInfo);
    const isLoading = customerInfo.status !== Status.FULFILLED;
    const isSubmitting = useIsNavigatingOrPending((state) => state.customerInfo.status);
    const navigate = useNavigate();
    const vehicle = customerInfo.me?.customer?.vehicles?.find((v) => v?.id === vehicleId);

    const notificationsData = useAppSelector((state) => state.notifications);
    const unreadNotifications = notificationsData.notifications?.filter((notification) => !notification?.read);
    const unreadVehicleNotifications =
        unreadNotifications?.filter((notification) => notification?.type === NotificationType.Vehicle) ?? [];
    const unreadVehicleNotificationsForVehicle = unreadVehicleNotifications.filter(
        (notification) => notification?.vehicle_id === vehicle?.id,
    );

    const unreadNotification = unreadVehicleNotificationsForVehicle?.length
        ? unreadVehicleNotificationsForVehicle[0]
        : null;

    const [isDeleteVehicleModalOpen, setIsDeleteVehicleModalOpen] = useState(false);
    const [tires, setTires] = useState(vehicle?.tires ?? 'SUMMER_ONLY');

    if (isLoading) {
        return <CenteredSpinner />;
    }

    if (!vehicle) {
        return <div>404</div>;
    }

    const tiresSelect = () => (
        <select
            style={{
                border: 'none',
                borderRadius: 0,
                fontFamily: 'inherit',
                fontSize: '1.0665rem',
                lineHeight: '1.6665rem',
                outline: 'none',
            }}
            name="tires"
            value={tires ?? null}
            disabled={isSubmitting}
            onChange={(e) => setTires(e.target.value)}
        >
            <option value={'SUMMER_ONLY'}>{transString('enums.tires.SUMMER_ONLY')}</option>
            <option value={'WINTER_ONLY'}>{transString('enums.tires.WINTER_ONLY')}</option>
            <option value={'SUMMER_AND_WINTER'}>{transString('enums.tires.SUMMER_AND_WINTER')}</option>
            <option value={'ALL_SEASON'}>{transString('enums.tires.ALL_SEASON')}</option>
        </select>
    );

    return (
        <>
            {isDeleteVehicleModalOpen && (
                <DeleteVehicleModal
                    onCloseModal={() => setIsDeleteVehicleModalOpen(false)}
                    onRedirect={() => navigate('/vehicles')}
                    vehicle={vehicle}
                />
            )}

            {unreadVehicleNotificationsForVehicle != null && unreadVehicleNotificationsForVehicle.length !== 0 && (
                <Card
                    gray={true}
                    to={unreadNotification ? `/notifications/${unreadNotification.id}` : '/notifications'}
                    arrow={true}
                >
                    <FlexRow>
                        <FlexColumn alignment={Alignment.Start}>
                            <FlexRow>
                                <Icon iconClassName="fa-light fa-circle-info" outline={true} />
                                <strong style={{margin: '0 auto 0 1rem'}}>
                                    {__('vehicles.copy.notifications', {
                                        count: unreadVehicleNotificationsForVehicle.length.toString(),
                                        brand: vehicle.brand ?? '',
                                    })}
                                </strong>
                            </FlexRow>
                        </FlexColumn>
                    </FlexRow>
                </Card>
            )}

            <Form>
                <Switch
                    disabled={isSubmitting}
                    defaultChecked={vehicle.lease === true}
                    name={'lease'}
                    label={transString('vehicles.labels.lease')}
                />

                {/*TODO: add more data in API for more info*/}
                {/*TODO: translate when all data and actual copy is present*/}

                <p className="leading">
                    Jouw{' '}
                    <strong>
                        {vehicle.brand} {vehicle.model}
                    </strong>{' '}
                    komt uit <strong>{vehicle.buildyear}</strong>. De personenauto staat sinds {vehicle.buildyear} op
                    kenteken, is voorzien van een {vehicle.fuel_type?.toLowerCase()}-motor, heeft een kilometerstand van{' '}
                    <strong>{formatKm(vehicle.mileage ?? 0)}</strong>.
                </p>

                <VehicleSpecsGrid vehicle={vehicle} />

                <PageTitle>
                    <h2>{`${__('vehicles.titles.other_specs')} ${vehicle.brand}`}</h2>
                </PageTitle>

                {/*TODO: decide on which data*/}
                <Table isKeyValue={true}>
                    <TableRow>
                        <TableCell name={__('vehicles.table.brand')} value={vehicle.brand} />
                        <TableCell name={__('vehicles.table.model')} value={vehicle.model} />
                    </TableRow>
                    <TableRow>
                        <TableCell name={__('vehicles.table.buildyear')} value={vehicle.buildyear} />
                        <TableCell name={__('vehicles.table.fuel_type')} value={vehicle.fuel_type} />
                    </TableRow>
                    <TableRow>
                        <TableCell name={__('vehicles.table.color')} value={vehicle.color} />

                        {/*TODO: select*/}
                        <TableCell name={__('vehicles.table.tires')} value={tiresSelect()} />
                    </TableRow>
                </Table>
            </Form>

            {/*Optional children*/}
            {children}

            <Card outline={true}>
                <FlexRow>
                    <FlexColumn alignment={Alignment.Start}>
                        <FlexRow>
                            <HideElement size={'sm'}>
                                <Icon
                                    iconClassName="fa-light fa-triangle-exclamation"
                                    outline={false}
                                    style={{marginRight: '1rem'}}
                                />
                            </HideElement>
                            <strong style={{margin: '0 1rem 0 auto'}}>{__('vehicles.copy.delete')}</strong>
                        </FlexRow>
                    </FlexColumn>
                    <FlexColumn alignment={Alignment.End}>
                        <HideElement size={'sm'}>
                            <Button
                                type={'button'}
                                position={ButtonPosition.Right}
                                onClick={() => setIsDeleteVehicleModalOpen(true)}
                            >
                                <i className="fa-light fa-trash" />
                                {__('vehicles.buttons.delete')}
                            </Button>
                        </HideElement>
                        <ShowElement size={'sm'}>
                            <AltButton
                                type={'button'}
                                position={ButtonPosition.Right}
                                onClick={() => setIsDeleteVehicleModalOpen(true)}
                            >
                                <i className="fa-light fa-trash" />
                            </AltButton>
                        </ShowElement>
                    </FlexColumn>
                </FlexRow>
            </Card>
        </>
    );
}
